import { useTranslation } from 'react-i18next';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import { ScenarioType } from 'shared/enums/scenario-type';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import useScenario from 'features/scenario/hooks/scenario-hook';
import styles from 'features/scenario/linkable-scenario/views/linkable-scenarios-table.module.scss';
import Scenario from 'features/scenario/models/scenario';
import ScenarioName from 'features/scenario/views/scenario-name.component';

interface Props {
  onSelectedScenariosChanged: (scenarios: Scenario[]) => void;
  scenariosToHide: Scenario[];
}

const LinkableScenariosTable = ({ onSelectedScenariosChanged, scenariosToHide }: Readonly<Props>) => {
  const { t: tLinkableScenarios } = useTranslation('linkableScenarios');
  const { t: tScenario } = useTranslation('scenario');
  const { scenarios, isLoading, isSuccess } = useScenario();
  const { hasPermission } = usePermissionAuthorization();

  const columns: ColumnDefinition<Scenario, NestedKeyof<Scenario>>[] = [
    {
      key: 'scenarioType',
      label: tLinkableScenarios('linkableScenariosTable.column.name'),
      disableSort: true,
      renderCustomContentProvider: (scenario) => <ScenarioName scenarioType={scenario.scenarioType} />,
    },
  ];

  const hiddenAndLiveDataMergedList =
    scenarios
      ?.filter(
        (scenario) =>
          Object.values(ScenarioType).includes(scenario.scenarioType) &&
          !scenariosToHide.some((scenarioToHide) => scenarioToHide.id === scenario.id),
      )
      .sort((a, b) => tScenario(`scenarioName.${a.scenarioType}`).localeCompare(tScenario(`scenarioName.${b.scenarioType}`))) ?? [];

  return (
    <>
      {hasPermission(Permission.LinkScenario) && (
        <>
          {isLoading && <LoadingIndicator />}
          {isSuccess && scenarios && (
            <Table
              className={styles.heading}
              data={hiddenAndLiveDataMergedList.map<RowItem<Scenario>>((scenario) => ({ data: scenario }))}
              columns={columns}
              rowsAreSelectable
              onSelectedRowsChanged={(scenarios: Scenario[]) => onSelectedScenariosChanged(scenarios)}
              rowIdentifier={(item: Scenario) => item.id}
            />
          )}
        </>
      )}
    </>
  );
};

export default LinkableScenariosTable;
